import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import generateId from '../../utils/generateId';
import { FeaturedAttribute, } from '../FeaturedAttribute';
import { ImageBadge, } from '../ImageBadge';
import { ImageUnavailable } from '../ImageUnavailable';
import { ResponsiveImage, } from '../ResponsiveImage/ResponsiveImage';
import { CustomColorSwatch, Swatch, SwatchPicker, } from '../SwatchPicker';
import { TextLink } from '../TextLink';
import { TextLinkInline, } from '../TextLinkInline';
import { FavoriteButton, } from './FavoriteButton';
import { ImageCycler, } from './ImageCycler';
const contentSkeletonHeight = 24;
const additionalProductOptionsContainerStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  color: ${theme.colors.gray600};
  display: flex;
  flex-wrap: wrap;
`;
const additionalProductOptionStyles = css `
  &:first-child::before {
    content: '+';
    margin-right: calc(${theme.spacing.base} / 2);
  }

  &:not(:last-child)::after {
    color: ${theme.colors.gray400};
    content: '|';
    margin-left: ${theme.spacing.base};
    margin-right: ${theme.spacing.base};
  }
`;
const artistNameStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  color: ${theme.colors.gray600};
`;
const cardStyles = css `
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
`;
const customContainerStyles = css `
  display: flex;
  flex-wrap: wrap;
`;
const contentContainerStyles = css `
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.x2};
  padding-bottom: ${theme.spacing.x2};
  padding-left: ${theme.spacing.x2};
  padding-right: ${theme.spacing.x2};
  padding-top: ${theme.spacing.x2};
`;
const unitPriceStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  color: ${theme.colors.gray600};
`;
const iconButtonContainerStyles = css `
  order: 3;
  padding-bottom: ${theme.spacing.base};
  padding-left: ${theme.spacing.base};
  padding-right: ${theme.spacing.base};
  padding-top: ${theme.spacing.base};
  position: absolute;
  right: 0;
`;
const imageBadgeContainerStyles = css `
  order: 2;
  padding-bottom: 0;
  padding-left: ${theme.spacing.x2};
  padding-right: ${theme.spacing.x2};
  padding-top: ${theme.spacing.x2};
  position: absolute;
`;
const imageContainerStyles = css `
  aspect-ratio: 1/1;
  background-color: ${theme.colors.gray400};
  background-color: ${theme.colors.gray100};
  display: flex;
  position: relative;
`;
const imageStyles = css `
  aspect-ratio: 1/1;
  height: auto;
  order: 1;
  width: 100%;
`;
const imageAnchorContainerStyles = css `
  ${imageStyles}
  order: -1;
  overflow: clip;
  text-decoration: none;
`;
const merchLineStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  color: ${theme.colors.notice800};
`;
const priceTextStyles = css `
  ${theme.typeStyles.bodySmallStyles}
  ${theme.typeStyles.bodyBoldStyles}
`;
const productNameStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  ${theme.typeStyles.bodyBoldStyles}
  cursor: pointer;
  text-decoration: none;
`;
const promoTextStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  color: ${theme.colors.positive800};
`;
const skeletonContentContainerStyles = css `
  display: flex;
  flex-direction: column;
  padding-bottom: ${theme.spacing.x3};
  padding-left: ${theme.spacing.x2};
  padding-right: ${theme.spacing.x2};
  padding-top: ${theme.spacing.x3};
  row-gap: ${theme.spacing.x3};
`;
const skeletonContentLine1Styles = css `
  ${styleUtils.baseSkeletonStyles}
  height: ${styleUtils.rem(contentSkeletonHeight)};
  width: 100%;
`;
const skeletonContentLine2Styles = css `
  ${skeletonContentLine1Styles}
  border-radius: ${theme.spacing.x3};
  width: 45%;
`;
const skeletonImageStyles = css `
  ${styleUtils.baseSkeletonStyles}
  aspect-ratio: 1/1;
  border-radius: 0;
`;
const viewMoreStyles = css `
  ${theme.typeStyles.bodyExtraSmallStyles}
  align-items: center;
  color: ${theme.colors.gray600};
  display: flex;
  padding: ${theme.spacing.base};
  white-space: nowrap;
`;
const onChildClick = (event) => {
    event.stopPropagation();
};
const BaseProductCard = ({ additionalProductOptions, artistName, artistUrl, attributionText, customColorSwatchTooltipMinWidth, customColorSwatchTooltipText, featuredAttribute, hasCustomColorSwatch, imageLoading, isFavorite = false, isFlatPagerVisible, isSkeletonVisible, merchLine, onArtistClick, onCardClick, onFavoriteClick, onOrderSampleClick, onSelectedImageIndexChange = () => { }, onSwatchChange, priceText, productImageBadge, productImages, productName, productUrl, promoText, callToActionText, callToActionUrl, selectedImageIndex, selectedSwatch, swatchOptions, unitPrice, }) => {
    const containerRef = useRef(null);
    const swatchRef = useRef(null);
    const viewMoreRef = useRef(null);
    const [remainingSwatches, setRemainingSwatches,] = useState(0);
    const [visibleSwatches, setVisibleSwatches,] = useState(0);
    const totalSwatches = useMemo(() => (((swatchOptions === null || swatchOptions === void 0 ? void 0 : swatchOptions.length) || 0) + (hasCustomColorSwatch ? 1 : 0)), [
        hasCustomColorSwatch,
        swatchOptions,
    ]);
    const localSwatchOptions = useMemo(() => {
        var _a;
        return (hasCustomColorSwatch
            ? ((_a = (swatchOptions
                || [])) === null || _a === void 0 ? void 0 : _a.concat([
                {
                    color: 'customColor',
                    colorwayId: 'customColor',
                    value: 'customColor',
                },
            ]).flat())
            : swatchOptions);
    }, [
        hasCustomColorSwatch,
        swatchOptions,
    ]);
    const productImage = useMemo(() => {
        var _a, _b, _c;
        return (_jsx("a", Object.assign({ css: imageAnchorContainerStyles, href: productUrl }, { children: (productImages
                && ((_a = productImages[0]) === null || _a === void 0 ? void 0 : _a.src))
                ? (productImages.length === 1
                    ? (_jsx(ResponsiveImage, { alt: productImages[0].alt, css: imageStyles, fallbackComponent: _jsx(ImageUnavailable, {}), imageLoading: imageLoading, imageSources: [
                            {
                                src: (_b = productImages[0]) === null || _b === void 0 ? void 0 : _b.src,
                            },
                        ]
                            .concat(((_c = productImages[0]) === null || _c === void 0 ? void 0 : _c.srcAlt)
                            ? [
                                {
                                    src: productImages[0].srcAlt,
                                },
                            ]
                            : [])
                            .flat() }))
                    : (_jsx(ImageCycler, { imageLoading: imageLoading, images: productImages, isFlatPagerVisible: isFlatPagerVisible, onSelectedImageIndexChange: onSelectedImageIndexChange, selectedImageIndex: selectedImageIndex })))
                : (_jsx(ImageUnavailable, {})) })));
    }, [
        imageLoading,
        isFlatPagerVisible,
        onSelectedImageIndexChange,
        productImages,
        productUrl,
        selectedImageIndex,
    ]);
    const swatches = useMemo(() => {
        var _a;
        return (totalSwatches === 1
            ? undefined
            : ((_a = (remainingSwatches
                ? (localSwatchOptions === null || localSwatchOptions === void 0 ? void 0 : localSwatchOptions.slice(0, visibleSwatches))
                : localSwatchOptions)) === null || _a === void 0 ? void 0 : _a.map((swatchProps, index) => (_jsx("div", Object.assign({ onClick: onChildClick, ref: index === 0 ? swatchRef : undefined }, { children: swatchProps.colorwayId === 'customColor'
                    ? (_jsx(CustomColorSwatch, { tooltipMinWidth: customColorSwatchTooltipMinWidth, tooltipText: customColorSwatchTooltipText }))
                    : (_jsx(Swatch, Object.assign({}, swatchProps))) }), swatchProps.colorwayId || swatchProps.id || swatchProps.value)))));
    }, [
        customColorSwatchTooltipMinWidth,
        customColorSwatchTooltipText,
        localSwatchOptions,
        remainingSwatches,
        visibleSwatches,
        totalSwatches,
    ]);
    const swatchPickerName = useMemo(() => ((`swatchPicker-${artistName}-${productName}-${generateId()}`).replaceAll(' ', '-')), [
        artistName,
        productName,
    ]);
    const alternativeProductOptions = useMemo(() => (additionalProductOptions
        && (_jsx("div", Object.assign({ css: additionalProductOptionsContainerStyles }, { children: additionalProductOptions
                .map((additionalProductOption, index) => (_jsx("div", Object.assign({ css: additionalProductOptionStyles }, { children: _jsx("span", { children: additionalProductOption }) }), index + 1))) })))), [
        additionalProductOptions,
    ]);
    const swatchPicker = useMemo(() => (swatches
        && (_jsx(SwatchPicker, Object.assign({ layout: "custom", name: swatchPickerName, onChange: onSwatchChange, selectionType: "single", shape: "CIRCLE", size: "small", value: selectedSwatch }, { children: _jsxs("div", Object.assign({ css: customContainerStyles, ref: containerRef }, { children: [_jsx("div", Object.assign({ css: customContainerStyles }, { children: swatches })), remainingSwatches > 0
                        && (_jsx("div", Object.assign({ css: viewMoreStyles, ref: viewMoreRef }, { children: _jsxs("span", { children: ["+", remainingSwatches] }) })))] })) })))), [
        onSwatchChange,
        remainingSwatches,
        selectedSwatch,
        swatches,
        swatchPickerName,
    ]);
    const handleCardClick = useCallback((event) => {
        window.location.href = productUrl;
        if (onCardClick) {
            onCardClick(event);
        }
    }, [
        onCardClick,
        productUrl,
    ]);
    const handleArtistClick = useCallback((event) => {
        onChildClick(event);
        if (onArtistClick) {
            onArtistClick(event);
        }
    }, [
        onArtistClick,
    ]);
    const handleFavoriteClick = useCallback((event) => {
        onChildClick(event);
        if (onFavoriteClick) {
            onFavoriteClick(event);
        }
    }, [
        onFavoriteClick,
    ]);
    const onKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            window.location.href = productUrl;
        }
    }, [
        productUrl,
    ]);
    const getSwatchData = useCallback(() => {
        var _a, _b, _c;
        const containerWidth = ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width) || 0;
        const viewMoreWidth = ((_b = viewMoreRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width) || 0;
        const swatchWidth = ((_c = swatchRef.current) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect().width) || 0;
        const possibleSwatches = Math.floor((containerWidth - viewMoreWidth) / swatchWidth);
        const localVisibleSwatches = (((totalSwatches - possibleSwatches) > 0
            && viewMoreWidth === 0)
            ? possibleSwatches - 1
            : possibleSwatches);
        const localRemainingSwatches = totalSwatches - localVisibleSwatches;
        return ({
            remainingSwatches: Number.isNaN(localRemainingSwatches) ? 0 : localRemainingSwatches,
            visibleSwatches: Number.isNaN(localVisibleSwatches) ? 0 : localVisibleSwatches,
        });
    }, [
        totalSwatches,
    ]);
    const onUpdate = useCallback(() => {
        const { remainingSwatches, visibleSwatches, } = getSwatchData();
        setRemainingSwatches(remainingSwatches);
        setVisibleSwatches(visibleSwatches);
    }, [
        getSwatchData,
    ]);
    useEffect(() => {
        let requestAnimationFrameId;
        const queueOnUpdate = () => {
            cancelAnimationFrame(requestAnimationFrameId);
            requestAnimationFrameId = (window
                .requestAnimationFrame(() => {
                onUpdate();
            }));
        };
        const resizeObserver = (new ResizeObserver(queueOnUpdate));
        if (containerRef.current) {
            resizeObserver
                .observe(containerRef.current);
        }
        onUpdate();
        return () => {
            resizeObserver
                .disconnect();
        };
    }, [
        isSkeletonVisible,
        onUpdate,
    ]);
    if (isSkeletonVisible) {
        return (_jsxs("div", { children: [_jsx("div", { css: skeletonImageStyles }), _jsxs("div", Object.assign({ css: skeletonContentContainerStyles }, { children: [_jsx("div", { css: skeletonContentLine1Styles }), _jsx("div", { css: skeletonContentLine2Styles })] }))] }));
    }
    return (_jsxs("div", Object.assign({ css: cardStyles, onClick: handleCardClick, onKeyDown: onKeyDown, role: "button", tabIndex: 0 }, { children: [_jsxs("div", Object.assign({ css: imageContainerStyles }, { children: [productImage, productImageBadge
                        && (_jsx("div", Object.assign({ css: imageBadgeContainerStyles }, { children: _jsx(ImageBadge, { modifier: productImageBadge }) }))), _jsx("div", Object.assign({ css: iconButtonContainerStyles }, { children: _jsx(FavoriteButton, { isFavorite: isFavorite, onClick: handleFavoriteClick }) }))] })), _jsxs("div", Object.assign({ css: contentContainerStyles }, { children: [alternativeProductOptions
                        || swatchPicker, featuredAttribute
                        && (_jsx(FeaturedAttribute, Object.assign({}, featuredAttribute))), _jsxs("div", { children: [_jsx("a", Object.assign({ css: productNameStyles, href: productUrl }, { children: productName })), _jsxs("div", Object.assign({ css: artistNameStyles }, { children: [attributionText
                                        && (_jsxs("span", { children: [attributionText, ' '] })), _jsx(TextLinkInline, { hasUnderline: false, hasUnderlineOnHover: true, href: artistUrl, onClick: handleArtistClick, target: "_blank", text: artistName })] }))] }), (priceText
                        || unitPrice
                        || promoText)
                        && (_jsxs("div", { children: [priceText
                                    && (_jsx("div", Object.assign({ css: priceTextStyles }, { children: priceText }))), unitPrice
                                    && (_jsx("div", Object.assign({ css: unitPriceStyles }, { children: unitPrice }))), promoText
                                    && (_jsx("div", Object.assign({ css: promoTextStyles }, { children: promoText })))] })), merchLine
                        && (_jsx("div", Object.assign({ css: merchLineStyles }, { children: merchLine }))), callToActionText
                        && callToActionUrl
                        && (_jsx(TextLink, { href: callToActionUrl, icon: "arrow-left", iconAlignment: "left", onClick: onOrderSampleClick, size: "small", text: callToActionText }))] }))] })));
};
const MemoizedBaseProductCard = memo(BaseProductCard);
export { MemoizedBaseProductCard as BaseProductCard };
